import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import Logo from './Logo';
import { Button } from './ui/button';
import { useServices } from '../lib/cms';

const navItems = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/services', label: 'Services', hasDropdown: true },
  { to: '/industries', label: 'Industries' },
  { to: '/why-us', label: 'Why AQC' },
  { to: '/standards', label: 'Standards' },
  { to: '/resources', label: 'Resources' },
  { to: '/contact', label: 'Contact' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const services = useServices();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); setServicesOpen(false); }, [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-[0_1px_0_0_rgba(15,42,71,0.08)]' : 'bg-white/85 backdrop-blur'
      }`}
    >
      <div className="container-aqc flex items-center justify-between h-[86px] md:h-[92px] lg:h-[96px]">
        <Link to="/" className="flex items-center" aria-label="Apex Quality Control home">
          <span className="hidden lg:block"><Logo size={68} /></span>
          <span className="hidden md:block lg:hidden"><Logo size={58} /></span>
          <span className="block md:hidden"><Logo size={48} /></span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6" aria-label="Primary">
          {navItems.map((item) =>
            item.hasDropdown ? (
              <div
                key={item.to}
                className="relative"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <NavLink to={item.to} className={({ isActive }) =>
                  `flex items-center gap-1 py-2 text-[0.94rem] font-medium hover:text-aqc-red transition-colors ${isActive ? 'text-aqc-navy' : 'text-aqc-ink'}`
                }>
                  {item.label}
                  <ChevronDown size={14} className={`transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
                </NavLink>
                {servicesOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 w-[320px] z-50">
                    <div className="bg-white shadow-2xl border border-aqc-line rounded-md overflow-hidden">
                      {services.map((s) => (
                        <Link key={s.slug} to={`/services/${s.slug}`} className="block px-5 py-3 hover:bg-aqc-cream border-b border-aqc-line last:border-0 group">
                          <div className="font-medium text-aqc-navy group-hover:text-aqc-red transition-colors">{s.title}</div>
                          <div className="text-xs text-aqc-muted mt-0.5 line-clamp-1">{s.tagline}</div>
                        </Link>
                      ))}
                      <Link to="/services" className="block px-5 py-3 bg-aqc-cream text-aqc-red font-semibold text-sm">
                        View all services →
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `relative py-2 text-[0.94rem] font-medium hover:text-aqc-red transition-colors ${isActive ? 'text-aqc-navy after:content-[\'\'] after:absolute after:left-0 after:right-0 after:-bottom-0.5 after:h-[2px] after:bg-aqc-red' : 'text-aqc-ink'}`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden lg:block">
          <Link to="/contact">
            <Button className="bg-aqc-navy hover:bg-[hsl(var(--aqc-navy-2))] text-white h-10 px-5 rounded-md shadow-sm">
              Contact Us
            </Button>
          </Link>
        </div>

        <button className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-md border border-aqc-line" onClick={() => setOpen((v) => !v)} aria-label="Toggle menu">
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-aqc-line bg-white">
          <div className="container-aqc py-4 flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `px-3 py-2.5 rounded-md text-[0.95rem] font-medium ${isActive ? 'bg-aqc-cream text-aqc-navy' : 'text-aqc-ink hover:bg-aqc-cream'}`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link to="/contact" className="mt-2">
              <Button className="w-full bg-aqc-navy hover:bg-[hsl(var(--aqc-navy-2))] text-white">Contact Us</Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
